import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    serviceBanner?: InjectableCss;
    leftColumn?: GridItemProps;
    rightNav?: GridItemProps;
    customBannerZone?: InjectableCss;
}

export const newsPageViewStyles: NewsPageViewStyles = {
    newsWrapper: {},
    contentWrapper: {
        css: css`
            margin-top: 8px;
            .section-wrapper {
                display: flex;
                div {
                    width: 50%;
                }
            }

            .image-wrapper {
                order: 2;
                text-align: center;
                padding-left: 20px;
                p {
                    width: 100%;
                    height: 100%;
                }
                span[class^="LazyImageStyle"] {
                    width: 100%;
                    height: 100%;
                }
            }
            .contact-us {
                text-align: center;
                margin-top: 3rem;
                p {
                    font-weight: 600;
                }
                a {
                    background: #005cb9;
                    font-weight: bold;
                    font-size: 16px;
                    padding-top: 0.6rem;
                    padding-right: 2rem;
                    padding-bottom: 0.65rem;
                    padding-left: 2rem;
                    text-decoration: none;
                    border: none;
                    color: #fff !important;
                    span {
                        color: #fff !important;
                    }
                    &:hover {
                        color: #fff !important;
                        span {
                            color: #fff !important;
                        }
                    }
                }
            }

            @media (max-width: 767px) {
                .section-wrapper {
                    flex-direction: column;
                    div {
                        width: 100%;
                    }
                    div:nth-child(1) {
                        order: 2;
                    }
                    .image-wrapper {
                        order: 1;
                        padding-left: 0;
                        padding-bottom: 15px;
                    }
                }
            }
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
            color: #3666b1;
            .title {
                font-weight: normal;
                font-size: 18px;
            }
        `,
    },
    publishDateText: {
        css: css`
            border-bottom: 2px solid;
            padding-bottom: 10px;
            margin-bottom: 10px;
            font-size: 18px;
            display: block;
        `,
    },
    serviceBanner: {
        css: css`
            position: relative;
            margin: 0px -15px;
            h1 {
                background-color: rgba(51, 51, 51, 0.5);
                color: rgb(255, 255, 255);
                line-height: 40px;
                top: 50%;
                transform: translateY(-50%);
                position: absolute;
                height: 100%;
                width: 350px;
                padding: 20px;
                display: flex;
                flex-direction: column;
                -webkit-box-pack: center;
                justify-content: center;
                text-align: center;
                font-weight: normal;
                font-size: 40px;
                margin-bottom: 0;
                left: 0;
                span {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 20px;
                                    line-height: 1.5;
                                `,
                                css`
                                    font-size: 20px;
                                    line-height: 1.5;
                                `,
                                css`
                                    font-size: 20px;
                                    line-height: 1.5;
                                `,
                                css`
                                    font-size: 20px;
                                    line-height: 1.5;
                                `,
                            ],
                            "minMax",
                        );
                    }}
                }
            }
        `,
    },
    leftColumn: {
        width: [12, 12, 8, 8, 8],
        css: css`
            margin-top: 30px !important;
            [class^="LazyImageStyle"] {
                float: left;
                width: 66%;
                height: 178px;
                margin-right: 30px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    rightNav: {
        width: [12, 12, 4, 4, 4],
        css: css`
            margin-top: 30px !important;
            padding-left: 30px;
            div[class^="Wrapper"] {
                background: #f0f9ff;
            }
            h4 {
                padding: 10px 15px;
                background: #f0f9ff;
                border-bottom: 1px solid #d2dbea;
                color: #3666b1;
                font-weight: bold;
                font-size: 15px;
            }
            ul {
                margin-left: 15px;
                li {
                    padding: 3px 0;
                    line-height: 1.6;
                    background: rgb(240, 249, 255);
                    margin: 0px;
                    font-size: 14px;
                    list-style: none;
                    font-weight: 400;
                    a {
                        cursor: pointer;
                        transition: all 0.5s ease-in-out;
                        border: none;
                        background: none;
                        padding: 0.4rem 2rem 0.4rem 0;
                        font-weight: 500;
                        display: block;
                        &:hover {
                            color: rgb(46, 100, 176) !important;
                        }
                        span {
                            font-size: 14px;
                            color: #6d6e71 !important;
                            &:hover {
                                color: rgb(46, 100, 176) !important;
                            }
                        }
                    }
                }
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    customBannerZone: {
        css: css`
            position: relative;
            margin: 0px -15px;
        `,
    },
};

const styles = newsPageViewStyles;

const NewsPageView = ({ id, page }: Props) => {
    if (!page) {
        return null;
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.serviceBanner}>
                <Zone contentId={id} zoneName="serviceBanner"></Zone>
            </StyledWrapper>
            <StyledWrapper {...styles.customBannerZone}>
                <Zone contentId={id} zoneName="customBanner"></Zone>
            </StyledWrapper>
            <GridContainer>
                <GridItem {...styles.leftColumn}>
                    <StyledWrapper {...styles.newsWrapper} key={page.id}>
                        <StyledWrapper {...styles.dateAndAuthorWrapper}>
                            <StyledWrapper>
                                {page.fields["publishDate"] && (
                                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                        <LocalizedDateTime
                                            dateTime={new Date(page.fields["publishDate"])}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </Typography>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                            <StyledWrapper {...styles.contentWrapper}>
                                {parse(page.fields["newsContent"], parserOptions)}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightNav}>
                    <Zone contentId={id} zoneName="rightNav"></Zone>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NewsPageView),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
    },
};

export default widgetModule;
